/**
 * The composer's model picker: one question with a three-stop slider.
 *
 *   How careful should the assistant be?
 *   Faster replies                More careful
 *   Quick ──────── Balanced ──────── Thorough
 *   <what the stop is for, the model it uses, examples>
 *   ─────────────────────────────────────────
 *   Advanced: choose a specific model      ›
 *
 * Every stop is Auto with an `optimize_for`, and each runs one fixed model
 * (`model_id` on the server's `auto_modes`). Advanced swaps the popover's
 * content for a second screen, locked to the main screen's height, with the
 * catalog grouped as Fastest / Everyday / Most capable and a Back button;
 * Escape goes back rather than closing. "Extra thinking time" only shows for
 * a chosen model, since each stop sets it. `⌘/` moves to the next stop.
 */
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Pin
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";

import { Button } from "@/components/ui/button";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  AUTO_MODEL_ID,
  PROVIDER_LABEL,
  type AgentModel,
  type AutoMode,
  type ModelProvider,
  type OptimizeFor
} from "../types";

export const AUTO_LABEL = "Auto";

/** Fallback copy when the catalog did not send the stops (`AUTO_MODES` server-side). */
export const DEFAULT_AUTO_MODES: AutoMode[] = [
  {
    id: "cost",
    label: "Quick",
    model_id: "gpt-6-luna",
    description: "Fastest replies for simple questions and small tasks.",
    examples: [
      "Look up a receipt number or priority date",
      "Rename or reorder exhibits",
      "Answer a quick question about the case"
    ]
  },
  {
    id: "balanced",
    label: "Balanced",
    model_id: "gemini-3-7-flash",
    description:
      "Right for most work: steady drafting and everyday case tasks.",
    examples: [
      "Draft a cover letter or client email",
      "Summarize an RFE",
      "Fill in a form section from the client's documents"
    ]
  },
  {
    id: "intelligence",
    label: "Thorough",
    model_id: "gemini-4-argon",
    description:
      "Reads carefully and double-checks. Slower, best for high-stakes work.",
    examples: [
      "Review a full I-140 packet for inconsistencies",
      "Draft an RFE response",
      "Check evidence against each EB-1A criterion"
    ]
  }
];

/**
 * The reasoning variant a model runs at when thinking is on ("High").
 * Mirrors the effort the server asks each provider for (`case_agent/model.py`).
 */
export const EFFORT_LABEL: Record<ModelProvider, string> = {
  anthropic: "High",
  openai: "High",
  google_genai: "High"
};

/** "High" for a thinking-capable model while thinking is on, else nothing. */
export const variantOf = (m: AgentModel, thinking: boolean): string | null =>
  m.supports_thinking && thinking ? EFFORT_LABEL[m.provider] ?? "High" : null;

/** The Advanced screen's groups, in display order (a tier is a capability class). */
export const TIER_GROUPS: Array<{
  tier: NonNullable<AgentModel["tier"]>;
  label: string;
}> = [
  { tier: "efficient", label: "Fastest" },
  { tier: "standard", label: "Everyday" },
  { tier: "frontier", label: "Most capable" }
];

/** Catalog models grouped by tier, catalog order kept; untiered models last. */
export const groupByTier = (
  models: AgentModel[]
): Array<{ label: string; models: AgentModel[] }> => {
  const groups = TIER_GROUPS.map(({ tier, label }) => ({
    label,
    models: models.filter((m) => m.tier === tier)
  }));
  const other = models.filter(
    (m) => !TIER_GROUPS.some(({ tier }) => tier === m.tier)
  );
  return [...groups, { label: "Other", models: other }].filter(
    (g) => g.models.length > 0
  );
};

/** Models whose label (or id) contains the query, catalog order kept. */
export const filterModels = (
  models: AgentModel[],
  query: string
): AgentModel[] => {
  const q = query.trim().toLowerCase();
  if (!q) return models;
  return models.filter(
    (m) => m.label.toLowerCase().includes(q) || m.id.toLowerCase().includes(q)
  );
};

/** The stop after `current`, wrapping. */
export const nextStop = (
  current: OptimizeFor,
  modes: AutoMode[]
): OptimizeFor => {
  const at = modes.findIndex((m) => m.id === current);
  return modes[(at + 1) % modes.length]?.id ?? current;
};

/**
 * A stop label sits at its stop's position on the track; the end labels align
 * to the track's edges so they stay inside the popover.
 */
const stopAlign = (index: number, last: number): string => {
  if (index === 0) return "translate-x-0";
  if (index === last) return "-translate-x-full";
  return "-translate-x-1/2";
};

/** Used for the Advanced screen if the main screen could not be measured. */
const FALLBACK_SCREEN_HEIGHT = 360;

type View = "main" | "advanced";

type Props = {
  /** The picked catalog id; `null` is Auto. */
  model: string | null;
  thinking: boolean;
  optimizeFor: OptimizeFor;
  models: AgentModel[];
  autoModes?: AutoMode[];
  onModelChange: (model: string | null) => void;
  onThinkingChange: (thinking: boolean) => void;
  onOptimizeForChange: (optimizeFor: OptimizeFor) => void;
  disabled?: boolean;
  className?: string;
};

export const ModelSelect: FC<Props> = ({
  model,
  thinking,
  optimizeFor,
  models,
  autoModes,
  onModelChange,
  onThinkingChange,
  onOptimizeForChange,
  disabled,
  className
}) => {
  const modes = autoModes?.length ? autoModes : DEFAULT_AUTO_MODES;
  const current = models.find((m) => m.id === model) ?? null;
  const stopIndex = Math.max(
    0,
    modes.findIndex((m) => m.id === optimizeFor)
  );
  const stop = modes[stopIndex];
  const stopModel = models.find((m) => m.id === stop.model_id) ?? null;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("main");
  const [query, setQuery] = useState("");
  const thinkingId = useId();
  const [mainHeight, setMainHeight] = useState<number | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const advancedRowRef = useRef<HTMLButtonElement | null>(null);
  const latest = useRef({ model, optimizeFor, modes, onOptimizeForChange });
  latest.current = { model, optimizeFor, modes, onOptimizeForChange };

  const measureMain = useCallback((el: HTMLDivElement | null) => {
    if (el && el.offsetHeight > 0) setMainHeight(el.offsetHeight);
  }, []);

  const shown = useMemo(() => filterModels(models, query), [models, query]);
  const groups = useMemo(() => groupByTier(shown), [shown]);

  // ⌘/ (Ctrl+/ elsewhere) moves to the next stop while on Auto.
  useEffect(() => {
    if (!models.length) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "/" || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey)
        return;
      const cur = latest.current;
      if (cur.model !== null) return;
      e.preventDefault();
      cur.onOptimizeForChange(nextStop(cur.optimizeFor, cur.modes));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [models.length]);

  // Only Auto to offer: nothing to pick.
  if (!models.length) return null;

  const goAdvanced = () => {
    setView("advanced");
    requestAnimationFrame(() => searchRef.current?.focus());
  };
  const goMain = () => {
    setView("main");
    setQuery("");
    requestAnimationFrame(() => advancedRowRef.current?.focus());
  };

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && shown[0]) {
      e.preventDefault();
      onModelChange(shown[0].id);
    }
  };

  const variant = current ? variantOf(current, thinking) : null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setView("main");
          setQuery("");
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild disabled={disabled}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Model"
              data-testid="model-select"
              data-model={model ?? AUTO_MODEL_ID}
              className={cn(
                "h-7 gap-1 rounded-md px-1.5 text-sm font-medium text-fg-2 hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground",
                className
              )}
            >
              {current ? (
                <Pin className="size-3 opacity-70" aria-hidden />
              ) : (
                <Gauge className="size-3 opacity-70" aria-hidden />
              )}
              <span className="max-w-40 truncate">
                {current?.label ?? stop.label}
                {variant ? (
                  <span
                    className="ml-1 font-normal text-muted-foreground"
                    data-testid="model-thinking-on"
                  >
                    {variant}
                  </span>
                ) : null}
              </span>
              <ChevronDown className="size-3 opacity-60" aria-hidden />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="flex items-center gap-2">
          Change setting
          <KbdGroup>
            <Kbd>⌘</Kbd>
            <Kbd>/</Kbd>
          </KbdGroup>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-80 gap-0 overflow-hidden p-0"
        data-testid="model-menu"
        data-view={view}
        onEscapeKeyDown={(e) => {
          if (view === "advanced") {
            e.preventDefault();
            goMain();
          }
        }}
      >
        {view === "main" ? (
          <div ref={measureMain} data-testid="model-main">
            <div className="px-3 pt-3 pb-2">
              <div className="text-base font-medium">
                How careful should the assistant be?
              </div>
              <div className="text-sm text-muted-foreground">
                Applies to this conversation.
              </div>
            </div>
            {current ? (
              <div
                className="mx-3 mb-2 flex items-start gap-2 rounded-md bg-fill-1 px-2.5 py-2 text-sm"
                data-testid="model-pinned-banner"
              >
                <Pin className="mt-0.5 size-3 shrink-0" aria-hidden />
                <span className="flex-1">
                  Using {current.label} instead of the slider.
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-6 shrink-0 px-2 text-sm"
                  data-testid="model-back-to-auto"
                  onClick={() => onModelChange(null)}
                >
                  Back to the slider
                </Button>
              </div>
            ) : null}
            <div
              className={cn("px-3 pb-3", current && "opacity-50")}
              aria-disabled={current ? true : undefined}
            >
              <div className="mb-2 flex justify-between text-xs text-muted-foreground">
                <span>Faster replies</span>
                <span>More careful</span>
              </div>
              <Slider
                min={0}
                max={modes.length - 1}
                step={1}
                value={[stopIndex]}
                disabled={Boolean(current)}
                onValueChange={([i]) => {
                  const next = modes[i ?? stopIndex];
                  if (next && next.id !== optimizeFor)
                    onOptimizeForChange(next.id);
                }}
                data-testid="model-slider"
                thumbProps={{
                  "aria-label": "How careful the assistant should be",
                  "aria-valuetext": stop.label
                }}
              />
              <div className="relative mt-1.5 h-5 text-sm">
                {modes.map((m, i) => {
                  const last = modes.length - 1;
                  const pct = last ? (i / last) * 100 : 50;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      disabled={Boolean(current)}
                      aria-pressed={m.id === optimizeFor}
                      data-testid={`model-stop-${m.id}`}
                      onClick={() => {
                        if (m.id !== optimizeFor) onOptimizeForChange(m.id);
                      }}
                      style={{ left: `${pct}%` }}
                      className={cn(
                        "absolute top-0 rounded whitespace-nowrap text-muted-foreground hover:text-foreground disabled:pointer-events-none",
                        stopAlign(i, last),
                        m.id === optimizeFor && "font-medium text-foreground"
                      )}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <div
                className="mt-3 rounded-md bg-fill-1 px-2.5 py-2 text-sm leading-4.25"
                data-testid="model-stop-summary"
              >
                <p>{stop.description}</p>
                {stopModel ? (
                  <p
                    className="mt-1 text-muted-foreground"
                    data-testid="model-stop-model"
                  >
                    Uses {stopModel.label}
                  </p>
                ) : null}
                {stop.examples?.length ? (
                  <>
                    <p className="mt-1.5 text-xs font-medium text-muted-foreground">
                      Good for
                    </p>
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-muted-foreground">
                      {stop.examples.map((example) => (
                        <li key={example}>{example}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </div>
            <button
              ref={advancedRowRef}
              type="button"
              onClick={goAdvanced}
              data-testid="model-advanced-open"
              className="flex h-9 w-full items-center gap-1.5 border-t border-line-2 px-3 text-left text-sm text-muted-foreground outline-none hover:bg-fill-1 hover:text-foreground focus-visible:bg-fill-1"
            >
              <span className="flex-1">Advanced: choose a specific model</span>
              {current ? (
                <span className="max-w-32 truncate">{current.label}</span>
              ) : null}
              <ChevronRight className="size-3.5 opacity-60" aria-hidden />
            </button>
          </div>
        ) : (
          <div
            className="flex flex-col"
            style={{ height: mainHeight ?? FALLBACK_SCREEN_HEIGHT }}
            data-testid="model-advanced"
          >
            <div className="flex items-center gap-1 border-b border-line-2 px-1.5 py-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-0.5 px-1.5 text-sm"
                onClick={goMain}
                data-testid="model-advanced-back"
              >
                <ChevronLeft className="size-3.5" aria-hidden />
                Back
              </Button>
              <span className="text-base font-medium">Choose a model</span>
            </div>
            <div className="border-b border-line-2 px-3 py-2">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search models"
                aria-label="Search models"
                data-testid="model-search"
                className="w-full bg-transparent text-base leading-5 text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div
              role="radiogroup"
              aria-label="Models"
              className="min-h-0 flex-1 overflow-y-auto p-1"
              data-testid="model-list"
            >
              {groups.map((group) => (
                <div key={group.label} className="pb-1">
                  <div className="px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </div>
                  {group.models.map((m) => {
                    const selected = m.id === model;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        data-testid={`model-option-${m.id}`}
                        onClick={() => onModelChange(m.id)}
                        className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left outline-none hover:bg-muted focus-visible:bg-muted"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-2">
                            <span className="truncate text-base">
                              {m.label}
                            </span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {PROVIDER_LABEL[m.provider]}
                            </span>
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {m.description}
                          </span>
                        </span>
                        {selected ? (
                          <Check
                            className="mt-0.5 size-3.5 shrink-0"
                            data-testid="model-check"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ))}
              {!shown.length ? (
                <div
                  className="px-2 py-3 text-center text-sm text-muted-foreground"
                  data-testid="model-empty"
                >
                  No models match “{query.trim()}”
                </div>
              ) : null}
            </div>
            {current?.supports_thinking ? (
              <div className="flex items-center gap-2.5 border-t border-line-2 px-3 py-2">
                <Label
                  htmlFor={thinkingId}
                  className="flex flex-1 flex-col items-start gap-0 font-normal"
                >
                  <span className="text-sm">Extra thinking time</span>
                  <span className="text-xs leading-3.75 text-muted-foreground">
                    Slower, but works through hard problems step by step.
                  </span>
                </Label>
                <Switch
                  id={thinkingId}
                  size="sm"
                  checked={thinking}
                  onCheckedChange={onThinkingChange}
                  data-testid="model-thinking-toggle"
                />
              </div>
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
